import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatActionType } from '../../utils';
import { getActionIcon } from '../../../../../utils/actionIcons';
import { FaBell, FaCheck } from 'react-icons/fa';
import {
  ActionItem as StyledActionItem,
  ActionIcon,
  ActionDescription,
  ActionReminderContainer,
} from '../../styles';
import { MarkerAction } from '../../../../../types/marker';
import { useNotifications } from '../../../../../context/NotificationContext';

interface ActionItemProps {
  action: MarkerAction;
  isUserAction: boolean;
  isHighlighted: boolean;
  expandedActions: Set<string>;
  isReadOnly?: boolean;
  
  // Fonctions
  toggleActionExpansion: (actionId: string) => void;
  canToggleNotification?: (actionId: string) => boolean;
}

export const ActionItem: React.FC<ActionItemProps> = ({
  action,
  isUserAction,
  isHighlighted,
  expandedActions,
  toggleActionExpansion,
  canToggleNotification,
  isReadOnly = false
}) => {
  const Icon = getActionIcon(action.type);
  const [_showNotificationTooltip, _setShowNotificationTooltip] = useState(false);
  const [isNotificationRead, setIsNotificationRead] = useState(false);
  const [showDateTooltip, setShowDateTooltip] = useState(false);
  const [showReminderTooltip, setShowReminderTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const { updateNotification, state } = useNotifications();


  // Fermer les tooltips en cliquant ailleurs
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDateTooltip(false);
      setShowReminderTooltip(false);
    };

    if (showDateTooltip || showReminderTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDateTooltip, showReminderTooltip]);

  // Fermer automatiquement après 2.5 secondes
  useEffect(() => {
    if (showDateTooltip) {
      const timer = setTimeout(() => {
        setShowDateTooltip(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showDateTooltip]);

  useEffect(() => {
    if (showReminderTooltip) {
      const timer = setTimeout(() => {
        setShowReminderTooltip(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showReminderTooltip]);

  // Vérifier l'état de la notification au chargement
  useEffect(() => {
    if (action.id) {
      const relatedNotification = state.notifications.find(notif => 
        notif.actionId === action.id || 
        (notif.message && notif.message.includes(action.description))
      );
      
      if (relatedNotification && relatedNotification.status === 'read') {
        setIsNotificationRead(true);
      }
    }
  }, [action.id, action.description, state.notifications]);

  // Vérifier si l'utilisateur peut modifier cette notification
  const canToggle = !isReadOnly && (canToggleNotification ? canToggleNotification(action.id) : true);

  // Fonction pour marquer la notification comme traitée
  const handleMarkNotificationRead = async () => {
    // Vérifier si l'utilisateur peut modifier cette notification
    if (!canToggle) {
      console.warn(`L'utilisateur ne peut pas modifier la notification de l'action ${action.id}`);
      return;
    }
    
    if (!isNotificationRead && action.id) {
      setIsNotificationRead(true);
      
      // Trouver la notification correspondante dans le menu
      const relatedNotification = state.notifications.find(notif => 
        notif.actionId === action.id || 
        (notif.message && notif.message.includes(action.description))
      );
      
      if (relatedNotification) {
        // Marquer comme lue pour persistance dans Supabase
        await updateNotification(relatedNotification.id, {
          status: 'read'
        });
      }
    }
  };

  return (
    <StyledActionItem 
      key={action.id} 
      $isUserAction={isUserAction}
      $isHighlighted={isHighlighted}
    >
      {/* Mode affichage uniquement - édition supprimée */}
      <ActionIcon>
        <Icon />
      </ActionIcon>
      {/* Icône horloge à droite de l'icône grise */}
      <ActionReminderContainer 
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltipPosition({ 
            x: rect.right + 10, 
            y: rect.top - 10 
          });
          // Fermer l'autre tooltip s'il est ouvert
          setShowReminderTooltip(false);
          setShowDateTooltip(!showDateTooltip);
        }}
        style={{ marginRight: '8px', cursor: 'pointer', position: 'relative' }}
      >
        ⏰
        {showDateTooltip && createPortal(
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '6px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              zIndex: 999999,
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.3)',
              maxWidth: '200px'
            }}>
            {new Date(action.date).toLocaleDateString('fr-FR')} à {new Date(action.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>,
          document.body
        )}
      </ActionReminderContainer>
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
        {action.type === 'category_change' ? (
          <ActionDescription 
            $isExpanded={expandedActions.has(action.id)}
            onClick={() => toggleActionExpansion(action.id)}
            style={{ color: '#3498db', fontWeight: 'bold', flex: 1 }}
          >
            {action.description.includes('→') 
              ? action.description 
              : action.details && typeof action.details === 'object' 
                ? `${(action.details as any).oldCategoryName || 'Sans catégorie'} → ${(action.details as any).newCategoryName || 'Sans catégorie'}`
                : action.description}
          </ActionDescription>
        ) : action.type === 'create' ? (
          <ActionDescription 
            $isExpanded={expandedActions.has(action.id)}
            onClick={() => toggleActionExpansion(action.id)}
            style={{ flex: 1 }}
          >
            Création du marqueur
          </ActionDescription>
        ) : (
          <ActionDescription 
            $isExpanded={expandedActions.has(action.id)}
            onClick={() => toggleActionExpansion(action.id)}
            style={{ flex: 1 }}
          >
            {(() => {
              let displayText = action.description;
              if (action.type === 'update' && displayText.startsWith('update - ')) {
                displayText = displayText.substring(8); // Enlever "update - "
              }
              return `${formatActionType(action.type)}: ${displayText}`;
            })()}
          </ActionDescription>
        )}
        
        {/* Icône notification à droite du texte si action a une reminderDate */}
        {action.reminderDate && (
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
            <ActionReminderContainer 
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltipPosition({ 
                  x: rect.right + 10, 
                  y: rect.top - 10 
                });
                // Fermer l'autre tooltip s'il est ouvert
                setShowDateTooltip(false);
                setShowReminderTooltip(!showReminderTooltip);
              }}
              style={{ cursor: 'pointer', position: 'relative' }}
            >
              <FaBell style={{ color: '#ffc107', fontSize: '15px' }} />
              {showReminderTooltip && createPortal(
                <div 
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'fixed',
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    zIndex: 999999,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    maxWidth: '200px'
                  }}>
                  {new Date(action.reminderDate).toLocaleDateString('fr-FR')} à {new Date(action.reminderDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>,
                document.body
              )}
            </ActionReminderContainer>
            {/* Rond check rouge/vert à droite de la cloche */}
            <div
              onClick={canToggle ? handleMarkNotificationRead : undefined}
              style={{
                marginLeft: '4px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: isNotificationRead ? '#28a745' : '#dc3545',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: !canToggle ? 'not-allowed' : (isNotificationRead ? 'default' : 'pointer'),
                transition: 'background-color 0.2s ease',
                opacity: !canToggle ? 0.5 : (isNotificationRead ? 0.8 : 1)
              }}
              title={
                !canToggle 
                  ? 'Vous ne pouvez pas modifier cette notification (elle appartient à un autre utilisateur)' 
                  : (isNotificationRead ? 'Notification traitée' : 'Notification non traitée - Cliquer pour marquer comme traitée')
              }
            >
              <FaCheck style={{ color: 'white', fontSize: '8px' }} />
            </div>
          </div>
        )}
      </div>
    </StyledActionItem>
  );
};
